import { postJson } from "./http";

/**
 * The ROSTER — who at the firm is on this client at all. Mappings of master
 * `users` rows onto the onboarding client; every endpoint answers with the full
 * roster state, and refusals arrive as readable sentences under `errors.message`
 * ("already a user…").
 *
 * Since the module-wise maker–checker rewrite this is the identity home and
 * nothing more: what somebody may DO comes from the seat they hold on a module
 * tab (seatsApi), never from a row here. The team screen reads `candidates` off
 * this state for its firm directory and calls `add` before seating anyone new,
 * because the server refuses a seat for anyone not on the roster.
 */

/**
 * One person on the client's roster: a master `users` row mapped onto the
 * client.
 *
 * `role`, `work_areas`, `reviewer_user_id` and `resolved_reviewer` are the
 * pre-rewrite columns the backend still resolves. Nothing on the team screen
 * reads them any more — the module tabs replaced all four — and they go when
 * the columns are dropped.
 */
export interface TeamMemberDTO {
  member_id: number;
  user_id: number;
  name: string;
  email: string;
  role: "accountant" | "senior_accountant" | "cfo";
  work_areas: string[];
  reviewer_user_id: number | null;
  resolved_reviewer: { user_id: number; name: string } | null;
}

export interface TeamCandidateDTO {
  user_id: number;
  name: string;
  email: string;
}

/** The whole screen in one payload — every team endpoint answers with this. */
export interface TeamStateDTO {
  members: TeamMemberDTO[];
  candidates: TeamCandidateDTO[];
  /** Always true since 2026-08-05: every client needs a CFO on the roster. */
  cfo_required: boolean;
}

interface TeamEnvelope {
  success: boolean;
  message: string;
  data: TeamStateDTO;
}

const teamCall = async (path: string, body: Record<string, unknown>): Promise<TeamStateDTO> =>
  (await postJson<TeamEnvelope>(path, body, ["message"])).data;

export const fetchTeamState = (publicId: string): Promise<TeamStateDTO> =>
  teamCall("/api/onboarding/team/state", { public_id: publicId });

export const addTeamMember = (publicId: string, userId: number): Promise<TeamStateDTO> =>
  teamCall("/api/onboarding/team/add", { public_id: publicId, user_id: userId });

/**
 * Creates the master `users` row (generated password, no company, no email
 * sent) and maps it onto the client — see OnboardingTeamController.
 */
export const inviteTeamMember = (publicId: string, name: string, email: string): Promise<TeamStateDTO> =>
  teamCall("/api/onboarding/team/invite", { public_id: publicId, name, email });

export const removeTeamMember = (publicId: string, memberId: number): Promise<TeamStateDTO> =>
  teamCall("/api/onboarding/team/remove", { public_id: publicId, member_id: memberId });
